import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { errorMessage, unwrap } from 'api/call'
import {
  deletePositionedBannersById,
  getPositionedBanners,
  getPositionedBannersByIdById,
  postPositionedBanners,
  putPositionedBannersById,
  type GetPositionedBannersResponse,
  type PositionedBannerInput,
  type PositionedBannerResponse,
} from 'api/generated'
import { toast } from 'sonner'

interface BannersState {
  loading: boolean
  error: string | null
  banner: PositionedBannerResponse | null
  banners: PositionedBannerResponse[] | null
  page: number
  totalPages: number
}

const initialState: BannersState = {
  loading: false,
  error: null,
  banner: null,
  banners: null,
  page: 1,
  totalPages: 0,
}

export const saveBanner = createAsyncThunk<
  PositionedBannerResponse,
  PositionedBannerInput,
  { rejectValue: string }
>('banners/save', async (body, { rejectWithValue }) => {
  try {
    const { positioned_banner } = await unwrap(postPositionedBanners({ body }))
    toast.success('Banner inserito con successo')
    return positioned_banner
  } catch (error) {
    toast.error("Impossibile eseguire l'operazione.")
    return rejectWithValue(errorMessage(error, 'Failed to save banner.'))
  }
})

export const fetchBanners = createAsyncThunk<
  GetPositionedBannersResponse,
  number,
  { rejectValue: string }
>('banners/fetchBanners', async (page, { rejectWithValue }) => {
  try {
    return await unwrap(getPositionedBanners({ query: { page } }))
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to fetch banners.'))
  }
})

export const deleteBanner = createAsyncThunk<
  PositionedBannerResponse,
  number,
  { rejectValue: string }
>('banners/deleteBanner', async (id, { rejectWithValue }) => {
  try {
    const { positioned_banner } = await unwrap(
      deletePositionedBannersById({ path: { id } })
    )
    toast.success('Banner eliminato con successo')
    return positioned_banner
  } catch (error) {
    toast.error("Impossibile eseguire l'operazione.")
    return rejectWithValue(errorMessage(error, 'Failed to delete banner.'))
  }
})

export const fetchBanner = createAsyncThunk<
  PositionedBannerResponse,
  number,
  { rejectValue: string }
>('banners/fetchBanner', async (id, { rejectWithValue }) => {
  try {
    return await unwrap(getPositionedBannersByIdById({ path: { id } }))
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to fetch banner.'))
  }
})

export const updateBanner = createAsyncThunk<
  void,
  { id: number; data: PositionedBannerInput },
  { rejectValue: string }
>('banners/updateBanner', async ({ id, data }, { rejectWithValue }) => {
  try {
    await unwrap(putPositionedBannersById({ path: { id }, body: data }))
    toast.success('Banner aggiornato con successo')
    window.location.href = '/gestione-banner'
  } catch (error) {
    toast.error("Impossibile eseguire l'operazione.")
    return rejectWithValue(errorMessage(error, 'Failed to update banner.'))
  }
})

const bannersSlice = createSlice({
  name: 'banners',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(saveBanner.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(saveBanner.fulfilled, (state, action) => {
        state.loading = false
        state.banners = [action.payload, ...(state.banners ?? [])]
      })
      .addCase(saveBanner.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to save banner.'
      })
      .addCase(fetchBanners.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBanners.fulfilled, (state, action) => {
        state.loading = false
        state.totalPages = action.payload.total_pages
        state.page = action.payload.page
        state.banners = action.payload.data
      })
      .addCase(fetchBanners.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to fetch banners.'
      })
      .addCase(deleteBanner.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteBanner.fulfilled, (state, action) => {
        state.loading = false
        if (state.banners) {
          state.banners = state.banners.filter(
            (banner) => banner.id !== action.meta.arg
          )
        }
      })
      .addCase(deleteBanner.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to delete banner.'
      })
      .addCase(fetchBanner.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBanner.fulfilled, (state, action) => {
        state.loading = false
        state.banner = action.payload
      })
      .addCase(fetchBanner.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to fetch banner.'
      })
      .addCase(updateBanner.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateBanner.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(updateBanner.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to update banner.'
      })
  },
})

export const bannersReducer = bannersSlice.reducer
